import { useEffect, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { EmployeeActionsBar } from './EmployeeActionsBar.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface EmployeeDetail {
  readonly empExtId: string;
  readonly employeeCode: string;
  readonly fullName?: string;
  readonly departmentName?: string;
  readonly designationName?: string;
  readonly statusCode?: string;
  readonly territoryCode?: string | null;
  readonly managerName?: string | null;
  readonly email?: string | null;
  readonly mobile?: string | null;
  readonly joinDate?: string | null;
  // Raw T036 fields returned by the real backend (fallbacks for the above).
  readonly employeeStatus?: string;
  readonly defaultTerritoryId?: string | null;
}

interface ReportingChainItem {
  readonly empExtId: string;
  readonly fullName: string;
  readonly designationName: string;
}

export function EmployeeDetailPage(): ReactNode {
  const { empExtId } = useParams<{ empExtId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { day } = useBusinessDay();
  const [emp, setEmp] = useState<EmployeeDetail | null>(null);
  const [chain, setChain] = useState<ReportingChainItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      apiRequest<EmployeeDetail>(`/v1/employees/${empExtId}`, { token }),
      apiRequest<ReportingChainItem[]>(`/v1/employees/${empExtId}/reporting-chain`, { token }).catch(() => [] as ReportingChainItem[]),
    ])
      .then(([detail, rc]) => { setEmp(detail); setChain(Array.isArray(rc) ? rc : []); })
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token, empExtId, reloadKey]);

  return (
    <main aria-label="employee-detail" style={{ padding: 24 }}>
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {loading && <p>{t('common.loading')}</p>}
      {emp && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h1>{emp.fullName ?? emp.employeeCode}</h1>
            <Link to="/org/employees" style={{ color: 'var(--color-text-link)' }}>{t('org.employee.detail.back')}</Link>
          </div>
          <EmployeeActionsBar empExtId={emp.empExtId} onChanged={() => setReloadKey(k => k + 1)} />
          <section aria-label="employee-info" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
            <div><strong>{t('org.employee.detail.code')}</strong> {emp.employeeCode}</div>
            <div><strong>{t('org.employee.detail.status')}</strong> <span style={{ padding: '2px 8px', borderRadius: 4, background: (emp.statusCode ?? emp.employeeStatus) === 'ACTIVE' ? 'var(--color-success-bg)' : 'var(--color-danger-bg)' }}>{emp.statusCode ?? emp.employeeStatus ?? '—'}</span></div>
            <div><strong>{t('org.employee.detail.department')}</strong> {emp.departmentName ?? '—'}</div>
            <div><strong>{t('org.employee.detail.designation')}</strong> {emp.designationName ?? '—'}</div>
            <div><strong>{t('org.employee.detail.territory')}</strong> {emp.territoryCode ?? emp.defaultTerritoryId ?? '—'}</div>
            <div><strong>{t('org.employee.detail.manager')}</strong> {emp.managerName ?? '—'}</div>
            <div><strong>{t('org.employee.detail.email')}</strong> {emp.email ?? '—'}</div>
            <div><strong>{t('org.employee.detail.mobile')}</strong> {emp.mobile ?? '—'}</div>
            <div><strong>{t('org.employee.detail.joinDate')}</strong> {day(emp.joinDate)}</div>
          </section>
          {chain.length > 0 && (
            <section aria-label="reporting-chain">
              <h2>{t('org.employee.detail.reportingChain')}</h2>
              <ol>
                {chain.map(c => <li key={c.empExtId}>{c.fullName} — {c.designationName}</li>)}
              </ol>
            </section>
          )}
        </>
      )}
    </main>
  );
}
